import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Button } from "@mui/material";
import { useTranslation } from "common/locales";
import { format } from "date-fns";
import { convertToFeedStockAmountString } from "features/feedPurchaseDetail/generalMethods";
import {
  convertToFertilizerAmountString,
  getFertilizerTypeString,
} from "features/fertilizerInput/FertilizerEventListItem";
import { convertToYieldAmountString } from "features/yieldInput/generalMethods";
import {
  AnimalGroup,
  ArableField,
  CropYieldAmount,
  Field,
  GrassYieldAmount,
  MeatYieldAmount,
  MilkYieldAmount,
  selectAllAnimalGroups,
  selectAllArableFields,
  selectAllFields,
  SOLD_OR_KEPT,
  useAppSelector,
  YIELD_TYPES,
} from "model";
import { CombinedEvent } from "./CombinedInputsOutputsList";

interface ExportButtonProps {
  events: CombinedEvent[];
  locale: string;
}

function escapeCsvField(field: string | undefined | null): string {
  if (!field) return "";
  // Escape quotes by doubling them, and wrap in quotes if contains comma, quote, or newline
  const stringValue = String(field);
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function getEventDetailsAsString(
  event: CombinedEvent,
  locale: string,
  t: (
    key: string,
    values?: Record<string, string | number | boolean | null | undefined>
  ) => string,
  fields: Field[],
  arableFields: ArableField[],
  animalGroups: AnimalGroup[]
): string {
  const allFields = [...fields, ...arableFields];

  switch (event.type) {
    case "reseed": {
      const reseedEvent = event.event;
      const eventFields = allFields.filter((f) =>
        reseedEvent.fieldGuids.includes(f.guid)
      );
      const fieldNames = eventFields.map((f) => f.name).join(", ");
      const grassType = t(
        `fieldinput.grassType.option.${reseedEvent.newGrassType}`
      );
      const seedingMethod = reseedEvent.seedingMethod
        ? t(`reseedEvent.seedingMethod.option.${reseedEvent.seedingMethod}`)
        : "";
      const parts = [
        `Field${eventFields.length > 1 ? "s" : ""}: ${fieldNames}`,
        `New grass type: ${grassType}`,
        seedingMethod && `Seeding method: ${seedingMethod}`,
        reseedEvent.note && `Note: ${reseedEvent.note}`,
      ].filter(Boolean);
      return parts.join("; ");
    }
    case "feed": {
      const feedEvent = event.event;
      const eventAnimalGroups = animalGroups.filter((ag) =>
        feedEvent.animalGroupGuids.includes(ag.guid)
      );
      const animalGroupNames = eventAnimalGroups
        .map((ag) => ag.name)
        .join(", ");
      const feedItems = feedEvent.feed
        .map((singleFeed) => {
          const feedType = t(
            `feedPurchase.feedType.option.${singleFeed.feedType}`
          );
          const amount = convertToFeedStockAmountString(
            singleFeed.amount,
            locale,
            singleFeed.unitType,
            t
          );
          return `${feedType}: ${amount}`;
        })
        .join(", ");
      const parts = [
        `Animal group${
          eventAnimalGroups.length > 1 ? "s" : ""
        }: ${animalGroupNames}`,
        `Feed: ${feedItems}`,
        feedEvent.note && `Note: ${feedEvent.note}`,
      ].filter(Boolean);
      return parts.join("; ");
    }
    case "feedPurchase": {
      const feedPurchaseEvent = event.event;
      const purchases = feedPurchaseEvent.feedPurchases
        .map((purchase) => {
          const feedType = t(
            `feedPurchase.feedType.option.${purchase.feedType}`
          );
          const amount = convertToFeedStockAmountString(
            purchase.amount,
            locale,
            purchase.unitType,
            t
          );
          const cost = purchase.cost ? `${purchase.cost.toFixed(2)} £` : "";
          return `${feedType} (${amount}${cost ? `, ${cost}` : ""})`;
        })
        .join("; ");
      const parts = [
        `Purchases: ${purchases}`,
        feedPurchaseEvent.note && `Note: ${feedPurchaseEvent.note}`,
      ].filter(Boolean);
      return parts.join("; ");
    }
    case "yield": {
      const yieldEvent = event.event;
      const parts: string[] = [];
      const yieldTypeLabel = t(
        `yieldEvent.yieldType.option.${yieldEvent.yieldDetails.yieldType}`
      );
      parts.push(`Type: ${yieldTypeLabel} yield`);

      let animalGroupsForYield: string[] = [];
      if (
        yieldEvent.yieldDetails.yieldType === YIELD_TYPES.MEAT ||
        yieldEvent.yieldDetails.yieldType === YIELD_TYPES.MILK
      ) {
        animalGroupsForYield = yieldEvent.yieldDetails.animalGroupGuids;
      }
      const selectedAnimalGroups = animalGroups.filter((ag) =>
        animalGroupsForYield.includes(ag.guid)
      );
      if (selectedAnimalGroups.length > 0) {
        const animalGroupNames = selectedAnimalGroups
          .map((ag) => ag.name)
          .join(", ");
        parts.push(
          `Animal group${
            selectedAnimalGroups.length > 1 ? "s" : ""
          }: ${animalGroupNames}`
        );
      }

      let grassFields: string[] = [];
      let arableFieldsForYield: string[] = [];
      if (YIELD_TYPES.GRASS === yieldEvent.yieldDetails.yieldType) {
        grassFields = yieldEvent.yieldDetails.fieldGuids;
      }
      if (YIELD_TYPES.CROP === yieldEvent.yieldDetails.yieldType) {
        arableFieldsForYield = yieldEvent.yieldDetails.fieldGuids;
      }

      const selectedGrassFields = fields.filter((f) =>
        grassFields.includes(f.guid)
      );
      const selectedArableFields = arableFields.filter((f) =>
        arableFieldsForYield.includes(f.guid)
      );

      if (selectedGrassFields.length > 0 || selectedArableFields.length > 0) {
        const allFieldNames = [
          ...selectedGrassFields.map((f) => f.name),
          ...selectedArableFields.map((f) => f.name),
        ].join(", ");
        parts.push(
          `Field${
            selectedGrassFields.length + selectedArableFields.length > 1
              ? "s"
              : ""
          }: ${allFieldNames}`
        );
      }

      const yieldAmounts = yieldEvent.yieldDetails.yieldAmounts.map(
        (
          singleYield:
            | MilkYieldAmount
            | GrassYieldAmount
            | MeatYieldAmount
            | CropYieldAmount
        ) => {
          const yieldString = convertToYieldAmountString(
            singleYield.amount,
            locale,
            singleYield.unitType,
            t
          );

          let yieldTypeDetail = "";
          if ("grassYieldType" in singleYield) {
            yieldTypeDetail = t(
              `yieldEvent.yieldType.option.${singleYield.grassYieldType}`
            );
          } else if ("cropYieldType" in singleYield) {
            yieldTypeDetail = t(
              `yieldEvent.yieldType.option.${singleYield.cropYieldType}`
            );
          }

          if (singleYield.soldOrKept === SOLD_OR_KEPT.KEPT) {
            const keptText = t("yieldEvent.amount.kept", {
              amount: yieldString,
            });
            return yieldTypeDetail
              ? `${yieldTypeDetail} - ${keptText}`
              : keptText;
          } else {
            const priceString = singleYield.revenue
              ? singleYield.revenue.toLocaleString(locale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  useGrouping: true,
                }) + " £"
              : "unknown revenue";
            const soldText = t("yieldEvent.amount.sold", {
              amount: yieldString,
              soldPrice: priceString,
            });
            return yieldTypeDetail
              ? `${yieldTypeDetail} - ${soldText}`
              : soldText;
          }
        }
      );

      if (yieldAmounts.length > 0) {
        parts.push(`Yield amounts: ${yieldAmounts.join("; ")}`);
      }

      if (yieldEvent.note) {
        parts.push(`Note: ${yieldEvent.note}`);
      }

      return parts.join("; ");
    }
    case "fertilizer": {
      const fertilizerEvent = event.event;
      const selectedGrassFields = fields.filter((f) =>
        fertilizerEvent.fieldGuids.includes(f.guid)
      );
      const selectedArableFields = arableFields.filter((f) =>
        fertilizerEvent.fieldGuids.includes(f.guid)
      );
      const fieldNames = [
        ...selectedArableFields.map((f) => f.name),
        ...selectedGrassFields.map((f) => f.name),
      ].join(", ");
      const fertilizerType = getFertilizerTypeString(fertilizerEvent, t);
      const fertilizerAmountString = convertToFertilizerAmountString(
        fertilizerEvent.totalAmount,
        locale,
        fertilizerEvent.totalAmountUnit,
        t
      );
      const parts = [
        `Type: ${fertilizerType}`,
        `Field${fieldNames.split(", ").length > 1 ? "s" : ""}: ${fieldNames}`,
        `Amount: ${fertilizerAmountString}`,
        fertilizerEvent.note && `Note: ${fertilizerEvent.note}`,
      ].filter(Boolean);
      return parts.join("; ");
    }
    case "fieldPreparation": {
      const fieldPrepEvent = event.event;
      const selectedGrassFields = fields.filter((f) =>
        fieldPrepEvent.fieldGuids.includes(f.guid)
      );
      const selectedArableFields = arableFields.filter((f) =>
        fieldPrepEvent.fieldGuids.includes(f.guid)
      );
      const fieldNames = [
        ...selectedGrassFields.map((f) => f.name),
        ...selectedArableFields.map((f) => f.name),
      ].join(", ");
      const machineryNames = fieldPrepEvent.machinery
        .map((machine) => t(`yieldEventInput.machinery.options.${machine}`))
        .join(", ");
      const parts = [
        `Field${fieldNames.split(", ").length > 1 ? "s" : ""}: ${fieldNames}`,
        `Fuel: ${fieldPrepEvent.fuel} litre`,
        machineryNames && `Machinery: ${machineryNames}`,
        fieldPrepEvent.note && `Note: ${fieldPrepEvent.note}`,
      ].filter(Boolean);
      return parts.join("; ");
    }
  }
}

function getEventTypeLabel(type: CombinedEvent["type"]): string {
  switch (type) {
    case "reseed":
      return "Reseed";
    case "feed":
      return "Feed";
    case "feedPurchase":
      return "Feed Purchase";
    case "yield":
      return "Yield";
    case "fertilizer":
      return "Fertilizer";
    case "fieldPreparation":
      return "Field Preparation";
  }
}

export default function ExportButton({ events, locale }: ExportButtonProps) {
  const { t } = useTranslation();
  const fields = useAppSelector(selectAllFields);
  const arableFields = useAppSelector(selectAllArableFields);
  const animalGroups = useAppSelector(selectAllAnimalGroups);

  const handleExport = () => {
    // Create CSV content
    const headers = ["Date", "Event Type", "Details"];
    const rows = events.map((event) => {
      const dateIsoString = event.date.toISOString();
      const eventType = getEventTypeLabel(event.type);
      const details = getEventDetailsAsString(
        event,
        locale,
        t,
        fields,
        arableFields,
        animalGroups
      );

      return [
        escapeCsvField(dateIsoString),
        escapeCsvField(eventType),
        escapeCsvField(details),
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `events-export-${format(new Date(), "yyyy-MM-dd-HHmmss")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button
      variant="outlined"
      startIcon={<FileDownloadIcon sx={{ width: 16, height: 16 }} />}
      onClick={handleExport}
      disabled={events.length === 0}
      sx={{
        height: "32px",
        fontSize: "14px",
        fontWeight: 500,
        textTransform: "none",
        backgroundColor: "#ffffff",
        borderColor: "#e4e4e7",
        color: "#18181b",
        borderRadius: "6px",
        padding: "8px 12px",
        gap: "8px",
        fontFamily: "Inter, sans-serif",
        "&:hover": {
          backgroundColor: "#fafafa",
          borderColor: "#e4e4e7",
        },
        "&:disabled": {
          backgroundColor: "#ffffff",
          borderColor: "#e4e4e7",
          color: "#a1a1aa",
        },
      }}
    >
      Export
    </Button>
  );
}
